// Recipes API - Real Backend Implementation
// Connects to Spring Boot backend at /api/recipes

// =============================================================================
// TYPES (JSDoc for better DX)
// =============================================================================

/**
 * @typedef {Object} Ingredient
 * @property {string} id - Unique identifier (e.g. "ing-1672531200000")
 * @property {string} name - Ingredient name (e.g. "Hähnchenfilet", "Tomaten")
 * @property {number} quantity - Base quantity amount (e.g. 300, 2)
 * @property {string} unit - Unit of measurement (e.g. "g", "Stück", "ml", "TL", "EL", "Liter", "Packung")
 * @property {string=} note - Optional note (e.g. "mittelgroß", "Bio", "frisch")
 * @property {string[]=} alternatives - Optional alternative ingredients
 * @property {number=} sortOrder - Position in ingredient list
 */

/**
 * @typedef {Object} Recipe
 * @property {string} id - Unique identifier (e.g. "recipe-1672531200000")
 * @property {string} bookmarkId - Associated bookmark ID
 * @property {string} title - Recipe title (e.g. "Goldene Hühnersuppe")
 * @property {string=} description - Optional description
 * @property {number} baseServings - Base number of servings (e.g. 3)
 * @property {string=} baseServingsRange - Display text for servings (e.g. "2-3 Portionen")
 * @property {Ingredient[]} ingredients - Array of ingredients
 * @property {string=} preparationTime - Preparation time (e.g. "15 min")
 * @property {string=} cookingTime - Cooking time (e.g. "45 min")
 * @property {string=} totalTime - Total time (e.g. "1 Stunde")
 * @property {string=} difficulty - Difficulty level (easy, medium, hard)
 * @property {string[]=} tags - Tags for categorization (e.g. ["vegan", "glutenfrei"])
 * @property {string=} imageUrl - URL to recipe image
 * @property {string=} sourceUrl - Original recipe URL (from bookmark)
 * @property {string} createdAt - ISO 8601 timestamp
 * @property {string} updatedAt - ISO 8601 timestamp
 * @property {string=} userId - User ID who created this recipe
 */

/**
 * @typedef {Object} ArchivedRecipe
 * @property {string} id - Unique identifier (e.g. "archive-1672531200000")
 * @property {string} bookmarkId - Associated bookmark ID
 * @property {string} title - Recipe title
 * @property {string=} url - Original URL
 * @property {string=} description - Description
 * @property {string} archivedAt - ISO 8601 timestamp
 * @property {RecipeSnapshot} snapshot - Snapshot of the recipe data
 * @property {string=} userId - User ID who archived this recipe
 */

/**
 * @typedef {Object} RecipeSnapshot
 * @property {string} title - Title at archive time
 * @property {string=} url - URL at archive time
 * @property {string=} description - Description at archive time
 * @property {string=} category - Category at archive time
 * @property {Ingredient[]=} ingredients - Ingredients if available
 * @property {number=} baseServings - Base servings if available
 */

/**
 * @typedef {Object} ShoppingCartItem
 * @property {string} recipeId - Recipe ID
 * @property {number} targetServings - Desired number of servings
 * @property {string} addedAt - ISO 8601 timestamp
 */

/**
 * @typedef {Object} ShoppingCart
 * @property {string} id - Cart ID
 * @property {ShoppingCartItem[]} items - Cart items
 * @property {string} updatedAt - ISO 8601 timestamp
 * @property {string=} userId - User ID
 */

/**
 * @typedef {Object} ScaledIngredient
 * @property {string} name - Ingredient name
 * @property {number} quantity - Scaled quantity
 * @property {number} originalQuantity - Original base quantity
 * @property {string} unit - Unit of measurement
 * @property {string=} note - Optional note
 * @property {string[]=} alternatives - Alternative ingredients
 */

/**
 * @typedef {Object} CreateRecipePayload
 * @property {string} bookmarkId - Associated bookmark ID (required)
 * @property {string} title - Recipe title (required)
 * @property {string=} description - Optional description
 * @property {number} baseServings - Base servings (required, default: 4)
 * @property {string=} baseServingsRange - Display text for servings
 * @property {Ingredient[]=} ingredients - Initial ingredients
 * @property {string=} preparationTime - Prep time
 * @property {string=} cookingTime - Cooking time
 * @property {string=} difficulty - Difficulty level
 * @property {string[]=} tags - Tags
 * @property {string=} imageUrl - Image URL
 * @property {string=} sourceUrl - Source URL
 */

/**
 * @typedef {Object} UpdateRecipePayload
 * @property {string=} title - Updated title
 * @property {string=} description - Updated description
 * @property {number=} baseServings - Updated base servings
 * @property {string=} baseServingsRange - Updated servings range text
 * @property {Ingredient[]=} ingredients - Updated ingredients
 * @property {string=} preparationTime - Updated prep time
 * @property {string=} cookingTime - Updated cooking time
 * @property {string=} difficulty - Updated difficulty
 * @property {string[]=} tags - Updated tags
 * @property {string=} imageUrl - Updated image URL
 */

/**
 * @typedef {Object} RecipeQueryParams
 * @property {string=} q - Search query (searches title, description, ingredients)
 * @property {string=} bookmarkId - Filter by bookmark ID
 * @property {string=} difficulty - Filter by difficulty (easy, medium, hard)
 * @property {string[]=} tags - Filter by tags (comma-separated)
 * @property {string=} sortBy - Sort field: "createdAt"|"updatedAt"|"title"|"baseServings" (default: "createdAt")
 * @property {string=} sortOrder - Sort order: "asc"|"desc" (default: "desc")
 * @property {number=} limit - Max number of results
 * @property {number=} offset - Pagination offset
 */

// =============================================================================
// CONSTANTS
// =============================================================================

const UNITS = ['g', 'kg', 'ml', 'l', 'Liter', 'Stück', 'TL', 'EL', 'Tasse', 'Prise', 'Packung', 'Dose', 'Stange', 'Bund', 'Scheibe', 'Zehe'];
const DIFFICULTIES = ['easy', 'medium', 'hard'];

// =============================================================================
// API CONFIGURATION
// =============================================================================

// API Base URL - uses environment variable or defaults to /api
const getBaseUrl = () => {
  return process.env.REACT_APP_API_URL || '/api';
};

// Get auth token from localStorage
const getAuthToken = () => {
  const token = localStorage.getItem('authToken');
  return token;
};

// Build headers with auth token
const getHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Generic fetch wrapper with error handling
async function apiFetch(endpoint, options = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: getHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Nicht autorisiert. Bitte erneut anmelden.');
    }
    if (response.status === 403) {
      throw new Error('Zugriff verweigert.');
    }
    if (response.status === 404) {
      throw new Error('Rezept nicht gefunden.');
    }
    if (response.status === 409) {
      throw new Error('Konflikt: Rezept existiert bereits.');
    }
    const errorText = await response.text();
    throw new Error(errorText || `HTTP Error: ${response.status}`);
  }

  // Handle empty responses (e.g., DELETE)
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }
  return { success: true };
}

// =============================================================================
// HELPER FUNCTIONS (exported for local use)
// =============================================================================

/**
 * Scale ingredient quantity based on target servings
 * @param {number} quantity - Original quantity
 * @param {number} baseServings - Original servings
 * @param {number} targetServings - Desired servings
 * @param {string} unit - Unit of measurement
 * @returns {number} Scaled and rounded quantity
 */
const scaleQuantity = (quantity, baseServings, targetServings, unit) => {
  const scale = targetServings / baseServings;
  const scaled = quantity * scale;

  // Rounding logic based on unit
  if (unit === 'g' || unit === 'ml') {
    return Math.round(scaled);
  } else if (unit === 'kg' || unit === 'l' || unit === 'Liter') {
    return Math.round(scaled * 100) / 100;
  } else if (unit === 'Stück' || unit === 'Stange' || unit === 'Scheibe' || unit === 'Zehe') {
    return Math.round(scaled * 2) / 2; // Round to 0.5
  } else if (unit === 'TL' || unit === 'EL') {
    return Math.round(scaled * 4) / 4; // Round to 0.25
  } else if (unit === 'Prise') {
    return Math.ceil(scaled); // Always round up for pinches
  } else {
    return Math.round(scaled * 100) / 100;
  }
};

/**
 * Scale all ingredients in a recipe
 * @param {Ingredient[]} ingredients - Original ingredients
 * @param {number} baseServings - Base servings
 * @param {number} targetServings - Target servings
 * @returns {ScaledIngredient[]} Scaled ingredients
 */
const scaleIngredients = (ingredients, baseServings, targetServings) => {
  return ingredients.map(ing => ({
    ...ing,
    originalQuantity: ing.quantity,
    quantity: scaleQuantity(ing.quantity, baseServings, targetServings, ing.unit)
  }));
};

// =============================================================================
// RECIPES API
// =============================================================================

/**
 * Recipes REST API
 *
 * Spring Boot Endpoint Mapping:
 *
 * RECIPES:
 * - GET    /api/recipes                         -> list()
 * - POST   /api/recipes                         -> create()
 * - GET    /api/recipes/{id}                    -> getById()
 * - PUT    /api/recipes/{id}                    -> update()
 * - DELETE /api/recipes/{id}                    -> remove()
 * - GET    /api/recipes/{id}/scaled             -> getScaledIngredients()
 * - POST   /api/recipes/{id}/ingredients        -> addIngredient()
 * - PUT    /api/recipes/{id}/ingredients/{ingId} -> updateIngredient()
 * - DELETE /api/recipes/{id}/ingredients/{ingId} -> removeIngredient()
 * - POST   /api/recipes/{id}/duplicate          -> duplicate()
 * - GET    /api/recipes/by-bookmark/{bookmarkId} -> getByBookmarkId()
 *
 * DETECTION:
 * - GET    /api/recipes/detect/keywords         -> getDetectionKeywords()
 * - POST   /api/recipes/detect/check            -> checkUrlForRecipe()
 * - POST   /api/recipes/detect/detect-all       -> detectAllRecipeUrls()
 *
 * ARCHIVE:
 * - GET    /api/recipes/archive                 -> listArchived()
 * - POST   /api/recipes/archive                 -> archiveRecipe()
 * - GET    /api/recipes/archive/{id}            -> getArchivedById()
 * - DELETE /api/recipes/archive/{id}            -> removeArchived()
 * - POST   /api/recipes/archive/{id}/restore    -> restoreFromArchive()
 *
 * SHOPPING CART:
 * - GET    /api/recipes/cart                    -> getCart()
 * - POST   /api/recipes/cart                    -> addToCart()
 * - DELETE /api/recipes/cart/{recipeId}         -> removeFromCart()
 * - DELETE /api/recipes/cart                    -> clearCart()
 * - POST   /api/recipes/cart/checkout           -> checkoutCart() -> creates ShoppingList
 *
 * UTILITIES:
 * - GET    /api/recipes/units                   -> getUnits()
 * - GET    /api/recipes/difficulties            -> getDifficulties()
 * - GET    /api/recipes/stats                   -> getStats()
 */
export const recipesApi = {
  // ===========================================================================
  // RECIPE CRUD
  // ===========================================================================

  /**
   * List recipes with optional filters, search, and sorting
   * REST: GET /api/recipes?q=&difficulty=&tags=&sortBy=createdAt&sortOrder=desc&limit=&offset=
   * @param {RecipeQueryParams} params - Query parameters
   * @returns {Promise<Recipe[]>}
   */
  async list(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.q) queryParams.append('q', params.q);
    if (params.bookmarkId) queryParams.append('bookmarkId', params.bookmarkId);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.tags) {
      const tagsString = Array.isArray(params.tags) ? params.tags.join(',') : params.tags;
      queryParams.append('tags', tagsString);
    }
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());

    const queryString = queryParams.toString();
    const endpoint = `/recipes${queryString ? `?${queryString}` : ''}`;

    return apiFetch(endpoint);
  },

  /**
   * Get a single recipe by ID
   * REST: GET /api/recipes/{id}
   * @param {string} id - Recipe ID
   * @returns {Promise<Recipe>}
   */
  async getById(id) {
    return apiFetch(`/recipes/${id}`);
  },

  /**
   * Get recipe by bookmark ID
   * REST: GET /api/recipes/by-bookmark/{bookmarkId}
   * @param {string} bookmarkId - Bookmark ID
   * @returns {Promise<Recipe|null>}
   */
  async getByBookmarkId(bookmarkId) {
    try {
      return await apiFetch(`/recipes/by-bookmark/${bookmarkId}`);
    } catch (error) {
      if (error.message.includes('nicht gefunden') || error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Create a new recipe
   * REST: POST /api/recipes
   * @param {CreateRecipePayload} payload
   * @returns {Promise<Recipe>}
   */
  async create(payload) {
    return apiFetch('/recipes', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Update an existing recipe
   * REST: PUT /api/recipes/{id}
   * @param {string} id - Recipe ID
   * @param {UpdateRecipePayload} payload
   * @returns {Promise<Recipe>}
   */
  async update(id, payload) {
    return apiFetch(`/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Delete a recipe
   * REST: DELETE /api/recipes/{id}
   * @param {string} id - Recipe ID
   * @returns {Promise<{success: boolean}>}
   */
  async remove(id) {
    return apiFetch(`/recipes/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Duplicate a recipe
   * REST: POST /api/recipes/{id}/duplicate
   * @param {string} id - Recipe ID to duplicate
   * @returns {Promise<Recipe>}
   */
  async duplicate(id) {
    return apiFetch(`/recipes/${id}/duplicate`, {
      method: 'POST',
    });
  },

  // ===========================================================================
  // INGREDIENTS
  // ===========================================================================

  /**
   * Get scaled ingredients for a recipe
   * REST: GET /api/recipes/{id}/scaled?targetServings=4
   * @param {string} id - Recipe ID
   * @param {number} targetServings - Desired number of servings
   * @returns {Promise<ScaledIngredient[]>}
   */
  async getScaledIngredients(id, targetServings) {
    return apiFetch(`/recipes/${id}/scaled?targetServings=${targetServings}`);
  },

  /**
   * Add an ingredient to a recipe
   * REST: POST /api/recipes/{id}/ingredients
   * @param {string} id - Recipe ID
   * @param {Ingredient} ingredient - Ingredient to add
   * @returns {Promise<Recipe>}
   */
  async addIngredient(id, ingredient) {
    return apiFetch(`/recipes/${id}/ingredients`, {
      method: 'POST',
      body: JSON.stringify(ingredient),
    });
  },

  /**
   * Update an ingredient in a recipe
   * REST: PUT /api/recipes/{id}/ingredients/{ingId}
   * @param {string} id - Recipe ID
   * @param {string} ingId - Ingredient ID
   * @param {Partial<Ingredient>} updates - Fields to update
   * @returns {Promise<Recipe>}
   */
  async updateIngredient(id, ingId, updates) {
    return apiFetch(`/recipes/${id}/ingredients/${ingId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  /**
   * Remove an ingredient from a recipe
   * REST: DELETE /api/recipes/{id}/ingredients/{ingId}
   * @param {string} id - Recipe ID
   * @param {string} ingId - Ingredient ID
   * @returns {Promise<Recipe>}
   */
  async removeIngredient(id, ingId) {
    return apiFetch(`/recipes/${id}/ingredients/${ingId}`, {
      method: 'DELETE',
    });
  },

  // ===========================================================================
  // RECIPE DETECTION
  // ===========================================================================

  /**
   * Get detection keywords for recipe URL detection
   * REST: GET /api/recipes/detect/keywords
   * @returns {Promise<{keywords: string[], patterns: string[]}>}
   */
  async getDetectionKeywords() {
    return apiFetch('/recipes/detect/keywords');
  },

  /**
   * Check if a URL likely contains a recipe
   * REST: POST /api/recipes/detect/check
   * @param {string} url - URL to check
   * @returns {Promise<{isRecipe: boolean, confidence: number, keywords: string[]}>}
   */
  async checkUrlForRecipe(url) {
    return apiFetch('/recipes/detect/check', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  },

  /**
   * Detect all recipe URLs from a list of bookmarks
   * REST: POST /api/recipes/detect/detect-all
   * @param {Array<{id: string, url: string, title: string}>} bookmarks - Bookmarks to check
   * @returns {Promise<Array<{bookmarkId: string, isRecipe: boolean, confidence: number}>>}
   */
  async detectAllRecipeUrls(bookmarks) {
    return apiFetch('/recipes/detect/detect-all', {
      method: 'POST',
      body: JSON.stringify({ bookmarks }),
    });
  },

  /**
   * Extract recipe data (ingredients, servings, etc.) from a URL
   * The backend will parse Schema.org/JSON-LD or HTML structure
   * REST: POST /api/recipes/extract-from-url
   * @param {string} url - URL to extract recipe from
   * @param {string} bookmarkId - Bookmark ID to associate with
   * @param {string} title - Recipe title (fallback if not found in URL)
   * @returns {Promise<Recipe>} - The created/updated recipe with extracted ingredients
   */
  async extractFromUrl(url, bookmarkId, title) {
    return apiFetch('/recipes/extract-from-url', {
      method: 'POST',
      body: JSON.stringify({ url, bookmarkId, title }),
    });
  },

  // ===========================================================================
  // RECIPE ARCHIVE
  // ===========================================================================

  /**
   * List archived recipes
   * REST: GET /api/recipes/archive?q=&sortBy=archivedAt&sortOrder=desc
   * @param {Object} params - Query parameters
   * @returns {Promise<ArchivedRecipe[]>}
   */
  async listArchived(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.q) queryParams.append('q', params.q);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const endpoint = `/recipes/archive${queryString ? `?${queryString}` : ''}`;

    return apiFetch(endpoint);
  },

  /**
   * Archive a recipe (create snapshot from bookmark)
   * REST: POST /api/recipes/archive
   * @param {Object} bookmark - Bookmark to archive
   * @returns {Promise<ArchivedRecipe>}
   */
  async archiveRecipe(bookmark) {
    return apiFetch('/recipes/archive', {
      method: 'POST',
      body: JSON.stringify(bookmark),
    });
  },

  /**
   * Get archived recipe by ID
   * REST: GET /api/recipes/archive/{id}
   * @param {string} id - Archive ID
   * @returns {Promise<ArchivedRecipe>}
   */
  async getArchivedById(id) {
    return apiFetch(`/recipes/archive/${id}`);
  },

  /**
   * Delete an archived recipe
   * REST: DELETE /api/recipes/archive/{id}
   * @param {string} id - Archive ID
   * @returns {Promise<{success: boolean}>}
   */
  async removeArchived(id) {
    return apiFetch(`/recipes/archive/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Restore recipe from archive (creates new recipe)
   * REST: POST /api/recipes/archive/{id}/restore
   * @param {string} id - Archive ID
   * @returns {Promise<Recipe>}
   */
  async restoreFromArchive(id) {
    return apiFetch(`/recipes/archive/${id}/restore`, {
      method: 'POST',
    });
  },

  // ===========================================================================
  // SHOPPING CART
  // ===========================================================================

  /**
   * Get current shopping cart
   * REST: GET /api/recipes/cart
   * @returns {Promise<ShoppingCart>}
   */
  async getCart() {
    return apiFetch('/recipes/cart');
  },

  /**
   * Add recipe to shopping cart
   * REST: POST /api/recipes/cart
   * @param {string} recipeId - Recipe ID to add
   * @param {number} targetServings - Desired servings
   * @returns {Promise<ShoppingCart>}
   */
  async addToCart(recipeId, targetServings) {
    return apiFetch('/recipes/cart', {
      method: 'POST',
      body: JSON.stringify({ recipeId, targetServings }),
    });
  },

  /**
   * Remove recipe from shopping cart
   * REST: DELETE /api/recipes/cart/{recipeId}
   * @param {string} recipeId - Recipe ID to remove
   * @returns {Promise<ShoppingCart>}
   */
  async removeFromCart(recipeId) {
    return apiFetch(`/recipes/cart/${recipeId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Clear shopping cart
   * REST: DELETE /api/recipes/cart
   * @returns {Promise<ShoppingCart>}
   */
  async clearCart() {
    return apiFetch('/recipes/cart', {
      method: 'DELETE',
    });
  },

  /**
   * Checkout cart - creates combined shopping list from all cart items
   * REST: POST /api/recipes/cart/checkout
   * @returns {Promise<{shoppingListId: string, items: ScaledIngredient[]}>}
   */
  async checkoutCart() {
    return apiFetch('/recipes/cart/checkout', {
      method: 'POST',
    });
  },

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Get available units
   * REST: GET /api/recipes/units
   * @returns {Promise<string[]>}
   */
  async getUnits() {
    try {
      return await apiFetch('/recipes/units');
    } catch {
      // Fallback to local constants if endpoint not available
      return [...UNITS];
    }
  },

  /**
   * Get difficulty levels
   * REST: GET /api/recipes/difficulties
   * @returns {Promise<string[]>}
   */
  async getDifficulties() {
    try {
      return await apiFetch('/recipes/difficulties');
    } catch {
      // Fallback to local constants if endpoint not available
      return [...DIFFICULTIES];
    }
  },

  /**
   * Get recipe statistics
   * REST: GET /api/recipes/stats
   * @returns {Promise<Object>}
   */
  async getStats() {
    return apiFetch('/recipes/stats');
  },

  // ===========================================================================
  // HELPER EXPORTS
  // ===========================================================================

  scaleIngredients,
  scaleQuantity
};

export default recipesApi;
